import React, { useState, useEffect, useRef } from 'react'
import {
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifFormField,
  MotifModalFooter,
  MotifButton,
  MotifSelect,
  MotifOption,
  MotifProgressLoader,
  MotifInput,
  MotifToast,
  MotifErrorMessage,
  MotifTooltip,
  MotifIcon,
} from '@ey-xd/motif-react'
import { IconoirInfoCircle } from '@ey-xd/motif-icon'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { pdfjs } from 'react-pdf'
import {
  useLazyGetEvidenceFileQuery,
  useGetAnnotationTypeQuery,
  useCreateAnnotationMutation,
  useLazyCancelAnnotateQuery,
  useLazyGetExtractedTextQuery,
  useVerifyAnnotateMutation,
} from '@/services/application/annotateReducer'
import { setEvidence } from '../DeveloperEvidence/DeveloperEvidenceSlice'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import {
  AnnotationViewerData,
  AnnotationPayload,
  AnnotateProps,
  AnnotateError,
  PDFFile,
  AnnotatePayload,
} from './type'
import { isImage, isPdf, ConstAnnotationType } from './Constant'
import { flattenChunksToRows, isOtherAnnotationTypeId } from './Component/helper'
import {
  addMarkAnnotate,
  updateMarkAnnotate,
  removeAnnotationByEvidenceId,
  clearDirtyForEvidence,
} from './AnnotateSlice'
import { activatePolling } from '@/features/annotationPolling/annotationPollingSlice'
import NotificationGrid from '@/components/Notification/NotificationGrid'
import ExtractedTextItem from './Component/ExtractedTextItem'
import pdfWorker from '/lib/pdfworker.js?url'
import './Annotate.scss'
import { UserFeedbackButtons } from '@/components/userFeedback/UserFeedbackButtons'
import { useSubmitUserFeedbackMutation, UserFeedbackReferenceType, SubmitUserFeedbackRequest } from '@/services/application/userFeedbackReducer'
import PdfReactAnnotator, { PdfRegion } from './Component/PdfReactAnnotator'
import ImageReactAnnotator, { ImageRegion } from './Component/ImageReactAnnotator'

// Extend the Window interface to include PDFViewerApplication
declare global {
  interface Window {
    PDFViewerApplication?: any;
  }
}

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker

export default function Annotation({
  fileName,
  evidenceId,
  isVisible,
  refetchEvidenceList
}: AnnotateProps) {
  const [file, setFile] = useState<PDFFile | null>(null)
  const [selectedAnnotateType, setSelectedAnnotateType] = useState<string>('')
  const [selectedOtherAnnotateType, setSelectedOtherAnnotateType] = useState<
    string | null
  >('')
  const dispatch = useDispatch()
  const [currentPDFPage, setCurrentPDFPage] = useState<number>(0)
  const [totalPDFPages, setTotalPDFPages] = useState<number>(1)
  const embeddedSourceAnnotations: any[] = []
  const [createAnnotate, { isLoading: annotateLoading }] =
    useCreateAnnotationMutation()
  const [getEvidenceFile, { isLoading: evidenceLoading }] =
    useLazyGetEvidenceFileQuery()
  const [cancelAnnotate] = useLazyCancelAnnotateQuery()
  const [getExtractedTextForResolve] = useLazyGetExtractedTextQuery()
  const [verifyAnnotate, { isLoading: verifyAnnotateLoading }] =
    useVerifyAnnotateMutation()
  const [saveDraftAnnotate, { isLoading: saveDraftLoading }] =
    useVerifyAnnotateMutation()
  const {
    data: annotationType,
    error: isAnnotateTypeError,
    isLoading: isAnnotateTypeLoading,
  } = useGetAnnotationTypeQuery('1')
  const annotationViewer = (window as any).CT.NITRO.AnnotationViewer
  const pdfViewer = (window as any).EY.CT.pdf
  // Use ref for annotation data — it drives the imperative viewer, not React UI
  const annotationListRef = useRef<AnnotationViewerData | undefined>(undefined)
  // Stable ref for the PDF viewer manager instance (survives re-renders)
  const pdfViewerManagerRef = useRef<any>(null)
  const annotationContainerRef = useRef<any>(null)
  // Track whether user selected an annotation type before PDF proxy was ready
  const pendingPdfActivationRef = useRef<boolean>(false)
  const annotationListStatus = useSelector(
    (state: any) => state.Annotate.filter((item: any) => item.evidenceId === evidenceId),
    shallowEqual,
  )
  const [annotationLoading, setAnnotationLoading] = useState<boolean>(false)
  const [isAnnotateError, setIsAnnotateError] = useState<AnnotateError>({
    isError: false,
    message: '',
  })
  const [currentSelectedAnnotationId, setCurrentSelectedAnnotationId] =
    useState<string>('')
  const [pdfRegions, setPdfRegions] = useState<PdfRegion[]>([])
  const [imageRegions, setImageRegions] = useState<ImageRegion[]>([])
  const [viewerReadOnly, setViewerReadOnly] = useState<boolean>(false)
  const [imageSrc, setImageSrc] = useState<string>('')
  const [imageRawSize, setImageRawSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 })
  const [draftToast, setDraftToast] = useState<{
    isShow: boolean
    type: 'success' | 'error'
    message: string
  }>({
    isShow: false,
    type: 'success',
    message: '',
  })
  const [submitFeedback] = useSubmitUserFeedbackMutation()
  const [showLeaveModal, setShowLeaveModal] = useState(false)
  const hasUnsavedChanges = (annotationListStatus as any[]).some((item: any) => item.isDirty)

  const terminalStatuses = ['Completed', 'Failed', 'TextExtractionFailed', 'TextEvaluationFailed']
  const hasPending = (annotationListStatus as any[]).some(
    (item: any) =>
      !terminalStatuses.includes(item.annotateStatus) &&
      (item.annotateList as any[]).some((a: any) => a.jobId),
  )
  const pendingCount = (annotationListStatus as any[]).filter(
    (item: any) =>
      !terminalStatuses.includes(item.annotateStatus) &&
      (item.annotateList as any[]).some((a: any) => a.jobId),
  ).length
  const hasFailedAnnotation = (annotationListStatus as any[]).some((item: any) =>
    ['Failed', 'TextExtractionFailed', 'TextEvaluationFailed'].includes(item.annotateStatus),
  )

  const isEvaluatedAnnotation = (item: any): boolean => {
    const hasPersistedRegion = Boolean(item?.annotateList?.[0]?.evidenceAnnotationId)
    const hasEvaluatedRows = Array.isArray(item?.extractedText) && item.extractedText.length > 0
    const isCompleted = item?.annotateStatus === 'Completed'
    return hasPersistedRegion && (hasEvaluatedRows || isCompleted)
  }

  // Reset state and clear drawn rects when modal is closed
  useEffect(() => {
    if (!isVisible) {
      // Clear transient viewer state so unsaved regions do not reappear on reopen.
      setPdfRegions([])
      setImageRegions([])
      setViewerReadOnly(false)
      setCurrentSelectedAnnotationId('')
      setTotalPDFPages(1)
      if (annotationListRef.current) {
        annotationListRef.current = {
          ...annotationListRef.current,
          annotationData: [],
        }
      }
      if (isPdf(fileName)) {
        try {
          pdfViewer.proxy?.clearDrawnRects?.()
        } catch (_) { /* proxy may not be ready */ }
      }
    }
  }, [isVisible])

  useEffect(() => {
    if (!draftToast.isShow) return
    const timer = setTimeout(() => {
      setDraftToast({ isShow: false, type: 'success', message: '' })
    }, 2500)

    return () => clearTimeout(timer)
  }, [draftToast])


  // Some annotations' extractedText in Redux is only populated once the user
  // manually clicks/edits that annotation's chip (see ExtractedTextItem.tsx).
  // An annotation whose job finished successfully but was never opened by the
  // user would otherwise be submitted with empty data. Fetch and fill those
  // in right before building the verify payload so nothing gets silently lost.
  const resolvePendingExtractedText = async (list: any[]) => {
    const itemsNeedingFetch = list.filter(
      (item: any) =>
        item.annotateStatus === 'Completed' &&
        (!Array.isArray(item.extractedText) || item.extractedText.length === 0) &&
        Array.isArray(item.annotateList) &&
        item.annotateList.length > 0,
    )

    if (itemsNeedingFetch.length === 0) return { list, failedCount: 0 }

    // Keyed by item identity (not evidenceAnnotationId) — annotateList[0] can
    // have an empty/falsy evidenceAnnotationId even when a later entry in the
    // same item's annotateList doesn't, which previously caused the write-back
    // step (keyed on annotateList[0]) to miss a result stored under a
    // different id and silently leave extractedText empty.
    const resolvedByItem = new Map<any, any[]>()
    let failedCount = 0
    await Promise.all(
      itemsNeedingFetch.map(async (item: any) => {
        const ids: string[] = item.annotateList
          .map((a: any) => a.evidenceAnnotationId)
          .filter(Boolean)
        if (ids.length === 0) return
        try {
          const responses = await Promise.allSettled(
            ids.map((id) => getExtractedTextForResolve(id).unwrap()),
          )
          if (responses.some((r) => r.status === 'rejected')) failedCount += 1
          const chunks: any[] = []
          responses.forEach((r) => {
            if (r.status === 'fulfilled' && Array.isArray((r.value as any)?.value)) {
              chunks.push(...(r.value as any).value)
            }
          })
          resolvedByItem.set(item, flattenChunksToRows(chunks, item.annotationTypeId))
        } catch {
          // Fetch failed entirely for this item — leave it unresolved (existing
          // empty extractedText) and flag it so the caller can block verify
          // instead of silently submitting empty values.
          failedCount += 1
        }
      }),
    )

    const resolvedList = list.map((item: any) => {
      if (resolvedByItem.has(item)) {
        return { ...item, extractedText: resolvedByItem.get(item) }
      }
      return item
    })

    return { list: resolvedList, failedCount }
  }

  const buildAnnotationPayload = (sourceList?: any[]) => {
    const payload: AnnotationPayload = {
      userDetails: [],
      userGroup: [],
      dateTime: [],
      permissions: [],
      other: [],
    }

    const list = sourceList ?? annotationListStatus

    Array.isArray(list) &&
      list.forEach((item: unknown) => {
        if (
          typeof item === 'object' &&
          item !== null &&
          'annotationTypeLabel' in item &&
          'extractedText' in item &&
          'annotateList' in item &&
          Array.isArray((item as any).annotateList) &&
          (item as any).annotateList.length > 0 &&
          'evidenceAnnotationId' in (item as any).annotateList[0]
        ) {
          const { annotationTypeLabel, extractedText } = item as {
            annotationTypeLabel: string
            extractedText: string[]
            annotateList: { evidenceAnnotationId: string }[]
          }
          const evidenceAnnotationId = (item as any).annotateList[0]
            .evidenceAnnotationId
          if (annotationTypeLabel === ConstAnnotationType.UserDetails) {
            payload['userDetails'].push({ userDetails: [...extractedText], id: evidenceAnnotationId })
          } else if (annotationTypeLabel === ConstAnnotationType.UserGroup) {
            payload['userGroup'].push({ userGroup: [...extractedText], id: evidenceAnnotationId })
          } else if (annotationTypeLabel === ConstAnnotationType.Permissions) {
            payload['permissions'].push({ permission: [...extractedText], id: evidenceAnnotationId })
          } else if (annotationTypeLabel === ConstAnnotationType.Other) {
            payload['other'].push({ other: [...extractedText], id: evidenceAnnotationId })
          } else if (annotationTypeLabel === ConstAnnotationType.DateAndTime) {
            payload['dateTime'].push({ date: [...extractedText], id: evidenceAnnotationId })
          }
        }
      })

      ; (['userDetails', 'userGroup', 'dateTime', 'other', 'permissions'] as const).forEach((key: string) => {
        const items = payload[key as keyof AnnotationPayload]

        if (items && items.length > 0) {
          items.forEach((item: any) => {
            if (item?.id === '') {
              delete item.id
            }
            ; (Object.keys(item) as Array<keyof typeof item>).forEach((opt) => {
              if (opt !== 'id') {
                item[opt] = cleanEmptyProperties(item[opt])
              }
            })
          })
        } else {
          switch (key) {
            case 'userDetails':
              payload.userDetails = [{ userDetails: [] }];
              break;
            case 'userGroup':
              payload.userGroup = [{ userGroup: [] }];
              break;
            case 'dateTime':
              payload.dateTime = [{ date: [] }];
              break;
            case 'other':
              payload.other = [{ other: [] }];
              break;
            case 'permissions':
              payload.permissions = [{ permission: [] }];
              break;
            default:
              break;
          }
        }
      })

    return payload
  }

  const handleAnnotationDataViewer = (imageBase64: any, dimension: any) => {
    //const annotationArray = embeddedSourceAnnotations
    const data: AnnotationViewerData = {
      annotationData: [],
      meta: {
        height: dimension.h,
        width: dimension.w,
      },
      pageImage:
        'data:image/png;base64,' +
        imageBase64.replace(/^data:application\/[a-z,-]+;base64,/, ''),
    }
    annotationListRef.current = data
  }

  // Sync annotation data to the imperative viewer (image or PDF)
  // Pass fileReady=true from contexts where file is known to be loaded but state hasn't updated yet
  // Returns a Promise for PDF so callers can chain activation after init completes
  const syncViewerAnnotations = (pageNumber?: number, fileReady?: boolean): Promise<any> | void => {
    const data = annotationListRef.current
    if (!data) return
    const hasFile = fileReady || !!file
    if (isImage(fileName) && hasFile) {
      if (annotationContainerRef.current) {
        annotationViewer.resetAnnotationViewer()
        annotationViewer
          .initAnnotationViewer(annotationContainerRef.current, data)
          .then((result: any) => {
            console.log('Annotation viewer initialized', result)
          })
      }
    } else if (isPdf(fileName) && hasFile) {
      if (!pdfViewer.proxy) return  // PDF viewer not ready yet; load callback will handle activation
      const page = pageNumber ?? currentPDFPage
      console.log('Current PDF Page:', page)
      return pdfViewer.proxy?.initAnnotationViewer(data, page).then((result: any) => {
        console.log('PDF Annotation viewer initialized', result)
      })
    }
  }

  useEffect(() => {
    const fetchEvidenceFile = async () => {
      const response = await getEvidenceFile(evidenceId).unwrap()
      setFile(response)
      if (response && isImage(fileName)) {
        const imageBase64 = await blobToBase64(response)
        const dimension = await getImageDimensions(
          'data:image/png;base64,' +
          imageBase64.replace(/^data:application\/[a-z,-]+;base64,/, ''),
        )
        handleAnnotationDataViewer(imageBase64, dimension)
        setImageSrc(
          'data:image/png;base64,' +
          imageBase64.replace(/^data:application\/[a-z,-]+;base64,/, ''),
        )
        setImageRawSize({ width: dimension.w, height: dimension.h })
        setCurrentPDFPage(1)
      } else if (response && isPdf(fileName)) {
        const imageBase64 = await blobToBase64(response)
        pdfViewer
          .webViewer({
            container: document.getElementById('pdf-viewer'),
            libpath: '/web',
            annotationBox: true,
            useReactOverlay: true,
          })
          .then(async (viewerManager: any) => {
            pdfViewerManagerRef.current = viewerManager
            // React overlay handles drawing; no activation of pdf internal annotation handler
            pendingPdfActivationRef.current = false
            viewerManager.load(imageBase64).then(() => {
              console.log('PDF loaded successfully')
              handleAnnotationDataViewer(imageBase64, { w: 0, h: 0 })
              setCurrentPDFPage(1)
              syncViewerAnnotations(1, true)
              // Page DOM elements may not be in the DOM yet at resolve time;
              // use the same 500ms delay the viewManager uses for its own button handlers
              setTimeout(() => {
                viewerManager.showOnlyCurrentPage(1)
                const iframe = document.getElementById('pdfViewerContainer') as HTMLIFrameElement | null
                const numPages = (iframe?.contentWindow as any)?.PDFViewerApplication?.pdfDocument?.numPages ?? 1
                setTotalPDFPages(numPages)
              }, 500)
            });

          })
      }
    }
    evidenceId && fetchEvidenceFile()
  }, [evidenceId])

  useEffect(() => {
    // Wait for the iframe to be loaded
    const iframe = document.getElementById('pdfViewerContainer') as HTMLIFrameElement | null;
    if (!iframe) return;
    // Define the handler
    const handlePrevious = () => {
      // Read actual page AFTER viewManager's showOnlyCurrentPage (500ms) has settled
      setTimeout(() => {
        const page = pdfViewer.proxy?.getCurrentActivePage?.()
        if (page && page >= 1) setCurrentPDFPage(page)
      }, 600)
    };

    const handleNext = () => {
      // Read actual page AFTER viewManager's showOnlyCurrentPage (500ms) has settled
      setTimeout(() => {
        const page = pdfViewer.proxy?.getCurrentActivePage?.()
        if (page && page <= totalPDFPages) setCurrentPDFPage(page)
      }, 600)
    };

    // Attach the event listener to the iframe's document
    const onIframeLoad = () => {
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.document.addEventListener('pdfjs-page-previous', handlePrevious);
        iframe.contentWindow.document.addEventListener('pdfjs-page-next', handleNext);
      }
    };

    // If iframe is already loaded, attach immediately
    if (iframe && iframe.contentWindow && iframe.contentWindow.document.readyState === 'complete') {
      onIframeLoad();
    } else if (iframe) {
      iframe.addEventListener('load', onIframeLoad);
    }

    // Cleanup
    return () => {
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.document.removeEventListener('pdfjs-page-previous', handlePrevious);
      }
      if (iframe) {
        iframe.removeEventListener('load', onIframeLoad);
      }
    };
  }, [currentPDFPage, totalPDFPages]);

  // Clear drawn PDF regions when all in-flight jobs for this evidence are resolved
  const prevHadPendingRef = useRef(false)
  useEffect(() => {
    if (prevHadPendingRef.current && !hasPending && isPdf(fileName)) {
      if (annotationListRef.current) {
        annotationListRef.current = { ...annotationListRef.current, annotationData: [] }
      }
      setPdfRegions([])
      setViewerReadOnly(false)
    }
    prevHadPendingRef.current = hasPending
  }, [hasPending])

  // (polling and updateJobStatus dispatch live in useAnnotationJobPoller in App.tsx)

  useEffect(() => {
    if (!selectedAnnotateType || !currentPDFPage) return
    const selectedAnnotation = annotationListStatus.find(
      (item: any) => item.annotationTypeId === selectedAnnotateType && item.pageNumber === currentPDFPage,
    )
    if (!selectedAnnotation) return
    setViewerReadOnly(isEvaluatedAnnotation(selectedAnnotation))
  }, [annotationListStatus, selectedAnnotateType, currentPDFPage])

  const handlePageChange = (page: number) => {
    const clamped = Math.max(1, Math.min(page, totalPDFPages))
    pdfViewerManagerRef.current?.showOnlyCurrentPage(clamped)
    setCurrentPDFPage(clamped)
  }

  const handleCancel = async (isCancelClicked: boolean) => {
    await doCancel(isCancelClicked)
  }

  const doCancel = async (isCancelClicked: boolean) => {
    setShowLeaveModal(false)
    setSelectedAnnotateType('')
    setSelectedOtherAnnotateType('')
    setCurrentPDFPage(0)
    if (isCancelClicked) {
      const cancelResponse = await cancelAnnotate(evidenceId)
      if (cancelResponse) {
        dispatch(setEvidence({ isModalOpen: false, evidenceId: '' }))
        dispatch(removeAnnotationByEvidenceId(evidenceId))
        refetchEvidenceList()
      }
    } else {
      dispatch(setEvidence({ isModalOpen: false, evidenceId: '' }))
      // Do NOT remove annotations from Redux — they survive so chips reappear on reopen
      refetchEvidenceList()
    }
  }

  const handleAnnotationVerify = async () => {
    // Guard against verifying while annotations are still being extracted —
    // submitting now would send empty values for in-flight annotations.
    if (hasPending) return
    // Annotations the user never opened/edited can still have empty
    // extractedText in Redux even though their job completed — resolve
    // those now so verify never submits empty data for a live annotation.
    const { list: resolvedList, failedCount } = await resolvePendingExtractedText(annotationListStatus as any[])
    if (failedCount > 0) {
      setDraftToast({
        isShow: true,
        type: 'error',
        message: `Unable to load extracted text for ${failedCount} annotation${failedCount !== 1 ? 's' : ''}. Please try again before verifying.`,
      })
      return
    }
    const payload = buildAnnotationPayload(resolvedList)
    const verifyResponse = await verifyAnnotate({ evidenceId, body: payload })
    if (verifyResponse) {
      dispatch(setEvidence({ isModalOpen: false, evidenceId: '' }))
      refetchEvidenceList()
    }
  }

  const handleSaveDraft = async () => {
    try {
      const payload = buildAnnotationPayload()
      await saveDraftAnnotate({ evidenceId, body: payload, draft: true }).unwrap()
      dispatch(clearDirtyForEvidence(evidenceId))
      setDraftToast({
        isShow: true,
        type: 'success',
        message: 'Draft saved to server successfully.',
      })
    } catch {
      setDraftToast({
        isShow: true,
        type: 'error',
        message: 'Unable to save draft to server. Please try again.',
      })
    }
  }

  const cleanEmptyProperties = (arr: Array<unknown>) => {
    const newArray: Record<string, unknown>[] = []
    for (const elm of arr) {
      if (typeof elm === 'object' && elm !== null) {
        const value: Record<string, unknown> = {}
        for (const key in elm as Record<string, unknown>) {
          if (
            (elm as Record<string, unknown>)[key] === null ||
            (elm as Record<string, unknown>)[key] === undefined ||
            (elm as Record<string, unknown>)[key] === ''
          ) {
            continue
          }
          value[key] = (elm as Record<string, unknown>)[key]
        }
        newArray.push(value)
      }
    }
    return newArray
  }

  const handleAddNewAnnotation = async () => {
    if (
      isOtherAnnotationTypeId(selectedAnnotateType) &&
      selectedOtherAnnotateType === ''
    ) {
      setSelectedOtherAnnotateType(null)
      return
    }

    const currentData = annotationListRef.current
    if (!currentData?.annotationData?.length) return

    const annotation = currentData.annotationData.map((ann: any, index: number) => ({
      annotateIndex: index,
      evidenceAnnotationId: '',
      jobId: '',
      topLeftX: ann.bBox[0].x1,
      topLeftY: ann.bBox[0].y1,
      bottomRightX: ann.bBox[0].x2,
      bottomRightY: ann.bBox[0].y2,
      status: '',
      pageNumber: ann?.pageNumber ? parseInt(ann.pageNumber) : 1,
    }))

    const selectedAnnotation = annotationType.filter((item: any) => item.id === selectedAnnotateType)
    annotation.forEach((annotationData) => {
      dispatch(
        addMarkAnnotate({
          evidenceId,
          annotationTypeId: selectedAnnotateType,
          annotationTypeLabel: selectedAnnotation[0].annotationTypeName,
          annotateList: [annotationData],
          pageNumber: currentPDFPage,
        }),
      )
    })

    const payloads: AnnotatePayload[] = annotation.map((annotationData: any) => {
      const payload: AnnotatePayload = {
        evidenceId,
        annotationTypeId: selectedAnnotateType,
        topLeftX: annotationData.topLeftX,
        topLeftY: annotationData.topLeftY,
        bottomRightX: annotationData.bottomRightX,
        bottomRightY: annotationData.bottomRightY,
        pageNumber: annotationData.pageNumber,
      }
      if (selectedOtherAnnotateType !== '' && selectedOtherAnnotateType !== null) {
        payload.AnnotationOtherTypeLabel = selectedOtherAnnotateType
      }
      console.log('[SOD Annotation] Queuing payload:', JSON.stringify(payload, null, 2), 'isPdf:', isPdf(fileName))
      return payload
    })

    setAnnotationLoading(true)
    const results = await Promise.allSettled(
      payloads.map((payload) => createAnnotate(payload).unwrap()),
    )
    setAnnotationLoading(false)

    const jobIds: string[] = []
    let allFailed = true
    results.forEach((result, i) => {
      if (result.status === 'fulfilled') {
        allFailed = false
        const { evidenceAnnotationId, jobId } = result.value
        dispatch(
          updateMarkAnnotate({
            index: annotation[i].annotateIndex,
            evidenceId,
            evidenceAnnotationId,
            jobId,
            annotationTypeId: selectedAnnotateType,
            pageNumber: currentPDFPage,
          }),
        )
        jobIds.push(jobId)
      } else {
        console.error(`[SOD Annotation] Region ${i} failed:`, result.reason)
        setIsAnnotateError({
          isError: true,
          message: result.reason?.data?.message ?? 'Error creating annotation',
        })
      }
    })

    if (jobIds.length > 0) {
      dispatch(activatePolling())
      const annotationTypeLabel = annotationType.find((t: any) => t.id === selectedAnnotateType)?.annotationTypeName ?? 'annotation'
      setDraftToast({
        isShow: true,
        type: 'success',
        message: `✓ Request for ${annotationTypeLabel} submitted. You may navigate away and check the status later.`,
      })
      // BE resets an already-Verified evidence back to Yet_To_Be_Verified when a
      // new annotation is added — refetch so the evidence list/badge reflect that
      // immediately instead of showing a stale "Verified" status.
      refetchEvidenceList()
    } else if (allFailed && isPdf(fileName)) {
      if (annotationListRef.current) {
        annotationListRef.current = { ...annotationListRef.current, annotationData: [] }
      }
      setPdfRegions([])
      setViewerReadOnly(false)
    }
  }

  const handleAnnotationType = (val: string) => {
    setCurrentSelectedAnnotationId('')
    const selectedType = val
    setSelectedAnnotateType(selectedType)

    // Clear any existing drawn annotations from the viewer
    const currentData = annotationListRef.current
    if ((currentData?.annotationData?.length ?? 0) > 0) {
      annotationListRef.current = currentData
        ? { ...currentData, annotationData: [], meta: currentData.meta, pageImage: currentData.pageImage }
        : { annotationData: [], meta: { width: 0, height: 0 }, pageImage: '' }
      // Clear drawn rects on the viewer directly
      if (isPdf(fileName)) {
        try { pdfViewer.proxy?.clearDrawnRects?.() } catch (_) { }
        setPdfRegions([])
      } else if (isImage(fileName)) {
        setImageRegions([])
      }
    }

    // If this type already has a completed annotation, show it in read-only mode
    const existingAnnotation = annotationListStatus.find(
      (item: any) =>
        item.annotationTypeId === selectedType &&
        item.pageNumber === currentPDFPage &&
        isEvaluatedAnnotation(item),
    )
    if (existingAnnotation) {
      // Show existing annotation and disable drawing
      setViewerReadOnly(true)
      setTimeout(() => {
        handleSelectedAnnotation(existingAnnotation.annotateList[0])
      }, 300)
    } else {
      setViewerReadOnly(false)
      // Enable drawing mode — use fileName to determine viewer type (NOT annotationViewer.imageElement
      // which stays stale from a previous image evidence session)
      setTimeout(() => {
        if (isImage(fileName)) {
          setImageRegions([])
        } else if (isPdf(fileName)) {
          pendingPdfActivationRef.current = false
        }
      }, 300)
    }
  }

  const blobToBase64 = async (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = (err) => reject(err)
      reader.readAsDataURL(blob)
    })
  }

  const getImageDimensions = (
    file: string,
  ): Promise<{ w: number; h: number }> => {
    return new Promise(function (resolved, rejected) {
      var i = new Image()
      i.onload = function () {
        resolved({ w: i.width, h: i.height })
      }
      i.src = file
    })
  }

  const handleSelectedAnnotation = (annotatedItem: any) => {
    // annotatedItem is a full Redux AnnotateStatusState entry.
    // Collect all sibling entries for the same annotationTypeId + pageNumber
    // (covers both: fresh batch with N items in annotateList, and DB-reload
    // where each region is a separate Redux entry).
    const siblingEntries: any[] = annotationListStatus.filter(
      (item: any) =>
        item.annotationTypeId === annotatedItem.annotationTypeId &&
        item.pageNumber === annotatedItem.pageNumber,
    )

    // Flatten all annotateList items across sibling entries
    const allAnnotateItems: any[] = siblingEntries.flatMap(
      (entry: any) => entry.annotateList ?? [],
    )

    const primary = allAnnotateItems[0] ?? {}
    const { evidenceAnnotationId } = primary
    setCurrentSelectedAnnotationId(evidenceAnnotationId ?? '')

    const prevData = annotationListRef.current
    annotationListRef.current = {
      annotationData: allAnnotateItems.map((item) => ({
        bBox: [
          {
            id: item.evidenceAnnotationId ?? `${Date.now()}`,
            x1: item.topLeftX,
            y1: item.topLeftY,
            x2: item.bottomRightX,
            y2: item.bottomRightY,
            comment: '',
            commentPos: { x: item.topLeftX, y: item.topLeftY, width: 350, height: 100 },
          },
        ],
      })),
      meta: { height: prevData?.meta.height ?? 0, width: prevData?.meta.width ?? 0 },
      pageImage: prevData?.pageImage ?? '',
    }

    setViewerReadOnly(true)

    if (isPdf(fileName)) {
      setPdfRegions(
        allAnnotateItems.map((item) => ({
          id: item.evidenceAnnotationId || `${Date.now()}-${Math.random()}`,
          pageNumber: item.pageNumber ? parseInt(item.pageNumber) : (annotatedItem.pageNumber ?? currentPDFPage),
          x1: item.topLeftX,
          y1: item.topLeftY,
          x2: item.bottomRightX,
          y2: item.bottomRightY,
          validated: item.validated,
        })),
      )
    } else if (isImage(fileName)) {
      setImageRegions(
        allAnnotateItems.map((item) => ({
          id: item.evidenceAnnotationId || `${Date.now()}-${Math.random()}`,
          pageNumber: 1,
          x1: item.topLeftX,
          y1: item.topLeftY,
          x2: item.bottomRightX,
          y2: item.bottomRightY,
          validated: item.validated,
        })),
      )
    }
  }

  const handlePdfRegionsChange = (regions: PdfRegion[]) => {
    setPdfRegions(regions)
    const currentData = annotationListRef.current
    const mappedAnnotationData = regions
      .filter((item) => item.pageNumber === currentPDFPage)
      .map((item) => ({
        pageNumber: String(item.pageNumber),
        bBox: [
          {
            id: item.id,
            x1: item.x1,
            y1: item.y1,
            x2: item.x2,
            y2: item.y2,
            comment: '',
            commentPos: { x: item.x1, y: item.y1, width: 350, height: 100 },
          },
        ],
      }))
    annotationListRef.current = currentData
      ? { ...currentData, annotationData: mappedAnnotationData }
      : {
        annotationData: mappedAnnotationData,
        meta: { width: 0, height: 0 },
        pageImage: '',
      }
  }

  const handleImageRegionsChange = (regions: ImageRegion[]) => {
    setImageRegions(regions)
    const currentData = annotationListRef.current
    const mappedAnnotationData = regions.map((item) => ({
      pageNumber: '1',
      bBox: [
        {
          id: item.id,
          x1: item.x1,
          y1: item.y1,
          x2: item.x2,
          y2: item.y2,
          comment: '',
          commentPos: { x: item.x1, y: item.y1, width: 350, height: 100 },
        },
      ],
    }))
    annotationListRef.current = currentData
      ? { ...currentData, annotationData: mappedAnnotationData }
      : {
        annotationData: mappedAnnotationData,
        meta: { width: imageRawSize.width, height: imageRawSize.height },
        pageImage: imageSrc,
      }
  }

  const handleFeedbackSubmit = (feedback: SubmitUserFeedbackRequest) => {
    if (currentSelectedAnnotationId) {
      feedback.referenceId = currentSelectedAnnotationId;
      // Here you can handle the feedback, e.g., send it to an API or log it
      submitFeedback(feedback)
        .unwrap()
        .then((response) => {
          console.log('Feedback submitted successfully:', response);
        })
        .catch((error) => {
          console.error('Error submitting feedback:', error);
        });
    }
  };

  const handleDeleteAnnotation = (annotationId: string) => {
    if (currentSelectedAnnotationId && annotationId === currentSelectedAnnotationId) {
      setCurrentSelectedAnnotationId('')
    }
    setPdfRegions((prev) => prev.filter((r) => r.id !== annotationId))
    setImageRegions((prev) => prev.filter((r) => r.id !== annotationId))
  };

  return (
    <>
      {/* Unsaved-changes leave guard */}
      <MotifModal
        show={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        size="sm"
      >
        <MotifModalHeader>Unsaved Changes</MotifModalHeader>
        <MotifModalBody>
          You're about to leave this section and any unsaved changes will be lost.
        </MotifModalBody>
        <MotifModalFooter>
          <MotifButton variant="secondary" onClick={() => setShowLeaveModal(false)}>
            Stay
          </MotifButton>
          <MotifButton variant="primary" onClick={() => doCancel(false)}>
            Leave anyway
          </MotifButton>
        </MotifModalFooter>
      </MotifModal>

      <MotifModal
        onClose={() => handleCancel(false)}
        show={isVisible}
        size="xl"
        focusTrapOptions={{
          tabbableOptions: {
            displayCheck: 'none',
          },
        }}
        className="annotate-evidence-modal"
      >
        <MotifModalHeader className="no-border-bottom">
          <div className="motif-h6-default-regular">{fileName}</div>
          <div
            className="motif-body2-default-regular motif-caption-default-light"
            style={{ display: 'flex' }}
          >
            Note
            <MotifTooltip
              allowHover
              hideCloseButton
              flip
              forceHideCounter={0}
              id="tooltip"
              offset={[0, 8]}
              placement="top"
              style={{ marginLeft: '10px' }}
              trigger={
                <MotifIcon
                  iconFunction={IconoirInfoCircle}
                  title="Evidence Annotation"
                  className="margin-left-5"
                />
              }
              usePortal
              variant="default"
            >
              <div className="motif-caption-default-light motif-tooltip-custom-width">
                Use the drop down menu to choose the field you want to annotate in the uploaded evidence.
              </div>
              <div className="motif-caption-default-light motif-tooltip-custom-width">
                For example, if you select “User Details,” move your cursor to the screenshot, then click and drag your mouse over the area containing that information to highlight it.
              </div>
              <div className="motif-caption-default-light motif-tooltip-custom-width">
                This step allows you to accurately capture and tag the relevant details from your screenshot. Ensure to review the output text and make any edits as needed. Once done, click “Verify”.
              </div>
            </MotifTooltip>
          </div>
        </MotifModalHeader>
        <MotifModalBody>
          {isAnnotateError.isError && (
            <NotificationGrid
              show={isAnnotateError.isError}
              type="error"
              message={isAnnotateError?.message}
            />
          )}
          <div className="annotate-container">
            {evidenceLoading}
            <div className="annotate-pdf-loader">
              {evidenceLoading ? (
                <MotifProgressLoader
                  role="alertdialog"
                  show
                  variant="default"
                  tip="Loading..."
                />
              ) : isImage(fileName) ? (
                <div className="container__annotation-view ">
                  <ImageReactAnnotator
                    enabled={Boolean(selectedAnnotateType !== '' || imageRegions.length > 0)}
                    imageSrc={imageSrc}
                    rawWidth={imageRawSize.width}
                    rawHeight={imageRawSize.height}
                    regions={imageRegions}
                    readOnly={viewerReadOnly}
                    onRegionsChange={handleImageRegionsChange}
                  />
                </div>
              ) : (
                <>
                  <div id="pdf-viewer" className="pdf-viewer">
                    <PdfReactAnnotator
                      enabled={Boolean(isPdf(fileName) && (selectedAnnotateType !== '' || pdfRegions.length > 0))}
                      pageNumber={currentPDFPage || 1}
                      pdfViewerProxy={pdfViewer.proxy}
                      regions={pdfRegions}
                      readOnly={viewerReadOnly}
                      onRegionsChange={handlePdfRegionsChange}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="annotate-mark">
              <div
                className="motif-body2-default-regular motif-caption-default-light"
                style={{ display: 'flex' }}
              >
                Information
                <MotifTooltip
                  allowHover
                  hideCloseButton
                  flip
                  forceHideCounter={0}
                  id="tooltip"
                  offset={[0, 8]}
                  placement="top"
                  style={{ marginLeft: '10px' }}
                  trigger={
                    <MotifIcon
                      iconFunction={IconoirInfoCircle}
                      title="Annotation Category"
                      className="margin-left-5"
                    />
                  }
                  usePortal
                  variant="default"
                >
                  <div className="motif-caption-default-light motif-tooltip-custom-width-35">
                    <span className="motif-tooltip-strong">User Details:</span> User Details dropdown captures key identifiers such as name, ID, email address, job title, department, permissions, and group memberships.
                  </div>
                  <div className="motif-caption-default-light motif-tooltip-custom-width-35">
                    <span className="motif-tooltip-strong">Permissions:</span> Permissions dropdown captures fields related to user name, permissions, and group memberships. Note: A permission is an assigned role or entitlement that defines the actions a user can perform within an application or production environment.
                  </div>
                  <div className="motif-caption-default-light motif-tooltip-custom-width-35">
                    <span className="motif-tooltip-strong">Group:</span> Group dropdown captures fields related to user name, permissions, and group memberships Note: Groups are linked to a user account (human or system), typically representing an Active Directory (AD) group or a nested group the user belongs to.
                  </div>
                  <div className="motif-caption-default-light motif-tooltip-custom-width-35">
                    <span className="motif-tooltip-strong">Date & Time:</span> Date & Time dropdown captures relevant fields, the system captures date-formatted text from the specified section
                  </div>
                  <div className="motif-caption-default-light motif-tooltip-custom-width-35">
                    <span className="motif-tooltip-strong">Other:</span> Other dropdown captures any additional text specified by the user for extraction
                  </div>
                </MotifTooltip>
              </div>

              <MotifFormField className="inline-form-field">
                <MotifSelect
                  ariaLabelledBy="select-label"
                  onChange={handleAnnotationType}
                  placeholder="Select"
                  visibleOptions={4}
                  disabled={annotateLoading}
                >
                  {annotationType &&
                    annotationType.map(
                      (option: {
                        id: string
                        annotationTypeName: string
                      }) => (
                        <MotifOption
                          key={option.id}
                          value={option.id}
                          disabled={annotationListStatus.some(
                            (item: any) =>
                              item.annotationTypeId === option.id &&
                              item.pageNumber === currentPDFPage
                          )}
                        >
                          {option.annotationTypeName}
                        </MotifOption>
                      ),
                    )}
                </MotifSelect>
                <MotifButton
                  onClick={() => handleAddNewAnnotation()}
                  size="medium"
                  type="button"
                  variant="primary"
                  disabled={annotateLoading || selectedAnnotateType === ''}
                  loading={annotateLoading}
                >
                  Mark
                </MotifButton>
              </MotifFormField>
              {isOtherAnnotationTypeId(selectedAnnotateType) && (
                  <div className="mb10px mt10px other-annotate-type">
                    <MotifInput
                      id="other-annotate-type"
                      onChange={(event) =>
                        setSelectedOtherAnnotateType(event.target.value)
                      }
                      value={selectedOtherAnnotateType ?? ''}
                      placeholder="Enter other annotation type"
                      type="text"
                    />
                    {selectedOtherAnnotateType === null && (
                      <MotifErrorMessage>
                        Please define others
                      </MotifErrorMessage>
                    )}
                  </div>
                )}
              {hasPending && (
                <div className="annotation-processing-banner">
                  <span>{pendingCount} annotation{pendingCount > 1 ? 's' : ''} processing…</span>
                </div>
              )}
              <div className="extracted-text">
                {draftToast.isShow && (
                  <MotifToast variant={draftToast.type}>
                    {draftToast.message}
                  </MotifToast>
                )}
                {hasFailedAnnotation && (
                  <MotifToast variant="error">
                    Text Extraction has Failed
                  </MotifToast>
                )}
                {evidenceId && file && (
                  <ExtractedTextItem
                    onSelectedAnnotation={handleSelectedAnnotation}
                    evidenceId={evidenceId}
                    currentPageNumber={currentPDFPage}
                    key={`${evidenceId}-${currentPDFPage}`}
                    onDeleteAnnotation={handleDeleteAnnotation}
                    onSystemAccountAcknowledged={refetchEvidenceList}
                  />
                )}
              </div>
            </div>
          </div>
        </MotifModalBody>
        <MotifModalFooter>
          <div className="annotation-footer-bar">
            <div className="annotation-footer-note motif-caption-default-light">
              {/* placeholder for footnote */}
            </div>
            <div className="annotation-footer-actions motif-row justify-content-end gap-1">
              {currentSelectedAnnotationId !== '' && (
                <UserFeedbackButtons referenceType={UserFeedbackReferenceType.Annotation} onFeedbackSubmit={handleFeedbackSubmit} />
              )}
              <MotifButton
                onClick={() => handleCancel(true)}
                size="medium"
                type="button"
                variant="secondary"
              >
                Cancel
              </MotifButton>
              <MotifButton
                onClick={handleSaveDraft}
                size="medium"
                type="button"
                variant="secondary"
                loading={saveDraftLoading}
              >
                Save draft
              </MotifButton>
              <MotifButton
                onClick={() => handleAnnotationVerify()}
                size="medium"
                type="button"
                variant="primary"
                loading={verifyAnnotateLoading}
                disabled={hasPending}
              >
                Verify
              </MotifButton>
            </div>
          </div>
        </MotifModalFooter>
      </MotifModal>
    </>
  )
}
